/**
 * Admin auth (sign-in + TOTP verify + sign-out). Backed by admin-api
 * /admin/auth/* routes.
 *
 * Storage shape:
 *   - `pendingToken` lives in module memory between sign-in and the
 *     TOTP step. Don't persist — it expires in 5 min anyway.
 *   - `bearer` (the real session token from totp-verify) lives in
 *     localStorage so reloads keep the session. Cleared on sign-out.
 *
 * The dev `X-Test-Admin-Id` header path in apps/admin/src/api/client.ts
 * still works for local dev. When a real bearer is in localStorage we
 * send it as Authorization too — production admin-api can swap to
 * Authorization-only by setting `ALLOW_TEST_ADMIN_HEADER=false`.
 */

import { useMutation } from '@tanstack/react-query';
import { identifyAdminPostHog, resetAdminPostHog } from '../lib/posthog.js';
import { ADMIN_API_BASE, apiFetch } from './client.js';

const BEARER_KEY = 'funkihoo.admin.bearer';

export function getStoredBearer(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(BEARER_KEY);
}

export function storeBearer(token: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(BEARER_KEY, token);
  // Identify the admin to PostHog once the real session is in
  // localStorage. Bearer shape from service-kit's
  // `createHmacSessionStore`: `<adminId_b64>.<role_b64>.<exp>.<nonce>.<sig>`.
  // First segment is base64url(adminId). Decode best-effort; skip
  // identify if shape doesn't match (old-style opaque tokens or
  // future changes).
  const adminId = tryExtractAdminId(token);
  if (adminId) identifyAdminPostHog(adminId);
}

export function clearBearer(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(BEARER_KEY);
  resetAdminPostHog();
}

function tryExtractAdminId(token: string): string | null {
  const parts = token.split('.');
  if (parts.length !== 5) return null;
  const first = parts[0];
  if (!first) return null;
  try {
    return atob(first.replace(/-/g, '+').replace(/_/g, '/'));
  } catch {
    return null;
  }
}

// ─── Step 1: sign-in ───────────────────────────────────────────────────

export interface SignInResponse {
  pendingToken: string;
}

export function useSignInMutation() {
  return useMutation({
    mutationFn: (args: { email: string; password: string }) =>
      apiFetch<SignInResponse>({
        method: 'POST',
        url: '/admin/auth/sign-in',
        body: args,
        baseUrl: ADMIN_API_BASE,
      }),
  });
}

// ─── Step 2: TOTP verify ───────────────────────────────────────────────

export interface VerifyResponse {
  token: string;
  role: string;
}

export function useTotpVerifyMutation() {
  return useMutation({
    mutationFn: (args: { pendingToken: string; code: string }) =>
      apiFetch<VerifyResponse>({
        method: 'POST',
        url: '/admin/auth/totp-verify',
        body: args,
        baseUrl: ADMIN_API_BASE,
      }),
  });
}

// ─── Sign-out ──────────────────────────────────────────────────────────

export function useSignOutMutation() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: true }>({
        method: 'POST',
        url: '/admin/auth/sign-out',
        body: {},
        baseUrl: ADMIN_API_BASE,
      }),
  });
}
